import React, { useMemo } from 'react'
import classNames from 'classnames'
import compact from 'lodash/compact'
import orderBy from 'lodash/orderBy'
import { Typography, Box, makeStyles } from 'transfix-ui/core'
import { IIssueResolutionStep } from 'types/graphqlTypes'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { brandColors, FormattedDateTime } from 'transfix-ui'
import { FORMATS } from 'constants/datetime'
import InlineEditDisplay, {
  IInlineEditDisplayProps,
} from '../../../CreateNewIssueModal/components/forms/InlineEditDisplay'
import { useUsersQuery } from '../graphql/Users'
import { stepsToResolveToResolutionSteps } from '../utils'
import { DisplayIssueStepsToResolve } from './DisplayIssueStepsToResolve'
import EditIssueStepsToResolve from './EditIssueStepsToResolve'

interface IIssueStepsProps {
  steps?: string[] | null
  resolutionSteps?: IIssueResolutionStep[] | null
  onSubmit?: IInlineEditDisplayProps['onSubmit']
  onEditChange?: IInlineEditDisplayProps['onEditChange']
  readOnly?: boolean
}

const useIssueStepsToResolveStyles = makeStyles(theme => ({
  completedText: {
    color: brandColors.green3,
  },
  tilteContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-end',
  },
}))

export const IssueStepsToResolve = ({
  resolutionSteps: resolutionStepsProp,
  steps: stepsProp,
  onSubmit = async () => {},
  onEditChange,
  readOnly = false,
}: IIssueStepsProps) => {
  const styles = useIssueStepsToResolveStyles()
  const steps = stepsProp || []
  const isMpStepsToResolveEnabled = useFeatureFlag('mpStepsToResolve')

  const resolutionSteps: Partial<IIssueResolutionStep>[] = resolutionStepsProp?.length
    ? resolutionStepsProp
    : stepsToResolveToResolutionSteps(steps)
  const finalSteps: Partial<IIssueResolutionStep>[] = isMpStepsToResolveEnabled
    ? resolutionSteps
    : steps.map((description, index) => ({ description, id: description, stepSequence: index + 1 }))
  const userIds = compact(finalSteps.map(({ completedById }) => `${completedById}`))
  const { data } = useUsersQuery({
    variables: {
      ids: userIds,
    },
    skip: userIds.length === 0,
  })
  const { searchTransfixAdminUsers } = data || {}
  const { nodes = [] } = searchTransfixAdminUsers || {}
  const users = compact(nodes)
  const finalStepsWithUsers = useMemo(
    () =>
      finalSteps.map(step => ({
        ...step,
        completedBy: users.find(({ id }) => id === `${step.completedById}`),
      })),
    [finalSteps, users]
  )

  const [lastUpdated] = orderBy(
    finalStepsWithUsers,
    ({ completedAt }) => new Date(completedAt || 0),
    'desc'
  )

  if (!isMpStepsToResolveEnabled) {
    return (
      <div>
        {steps.map((step, i) => (
          <Box key={step} pt={1}>
            {i + 1}. {step}
          </Box>
        ))}
        {steps.length === 0 && (
          <Typography variant='subtitle2' color='textSecondary'>
            No steps to resolve this issue have been added
          </Typography>
        )}
      </div>
    )
  }

  const completed = resolutionSteps.filter(({ completedAt }) => Boolean(completedAt)).length
  const completedPercent = resolutionSteps.length
    ? Math.round((completed / resolutionSteps.length) * 100)
    : 0

  return (
    <InlineEditDisplay
      name='issueResolutionSteps'
      dataTest='issueResolutionSteps-edit'
      editLabel='Steps To Resolve'
      sectionName='Edit Steps To Resolve'
      label={
        <div>
          <div className={styles.tilteContainer}>
            <span>Steps To Resolve </span>
            {Boolean(completedPercent) && (
              <Typography
                component='span'
                variant='body2'
                className={classNames({ [styles.completedText]: Boolean(completedPercent) })}>
                {completedPercent}% completed
              </Typography>
            )}
          </div>
          {lastUpdated?.completedAt && (
            <Typography variant='caption' color='textSecondary'>
              Last Updated by {lastUpdated.completedBy?.fullName}{' '}
              <FormattedDateTime
                inSentence
                showTimezone
                datetime={lastUpdated.completedAt}
                format='T24TimeWeekdayDateAndYear'
                displayTimezone={FORMATS.defaultTimeZone}
              />
            </Typography>
          )}
        </div>
      }
      onSubmit={onSubmit}
      onEditChange={onEditChange}
      readOnly={readOnly || !resolutionSteps?.some(({ id }) => Boolean(id))}
      displayValue={
        <DisplayIssueStepsToResolve steps={steps} resolutionSteps={finalStepsWithUsers} />
      }>
      <EditIssueStepsToResolve resolutionSteps={finalStepsWithUsers} />
    </InlineEditDisplay>
  )
}

export default IssueStepsToResolve
